import { useEffect, useState } from "react";
import { createViewUrl } from "@/lib/downloadFile";

/**
 * DeliverableChatImage — thumbnail inline da bolha do chat (PR-A F2): a
 * imagem gerada É o conteúdo principal da mensagem; a legenda curta fica no
 * texto da bolha. Resolve a própria signed URL no mount (createViewUrl —
 * mesma fonte do viewer/Biblioteca) e re-resolve UMA vez num erro de load
 * (signed URL de 300s pode expirar entre mount e scroll-back). Clique abre o
 * DeliverableImageViewer compartilhado via onOpen. <img> nativo também dá o
 * long-press → "Salvar em Fotos" do mobile web de graça.
 */

interface DeliverableChatImageProps {
  storagePath: string;
  title: string;
  onOpen: () => void;
  // P2: "thumb" = miniatura 36px pra row da Biblioteca (object-cover, sem
  // margem; falha vira caixa vazia quieta pra não colapsar o layout da row).
  // Default "chat" preserva o comportamento original da bolha.
  variant?: "chat" | "thumb";
}

const DeliverableChatImage = ({ storagePath, title, onOpen, variant = "chat" }: DeliverableChatImageProps) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  const thumb = variant === "thumb";

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    createViewUrl(storagePath)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        console.error("[DeliverableChatImage] sign failed:", err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storagePath, retried]);

  // Falhou de vez (sign ou load após retry): chat degrada pra só a legenda
  // (sem thumbnail quebrado poluindo a bolha); thumb mantém a caixa de 36px
  // vazia pra row da Biblioteca não colapsar.
  if (failed) {
    return thumb
      ? (
        <div
          className="w-9 h-9 rounded-xl shrink-0"
          style={{ background: "hsl(222 18% 14%)", border: "1px solid hsl(222 18% 22%)" }}
        />
      )
      : null;
  }

  if (!url) {
    return (
      <div
        className={thumb
          ? "w-9 h-9 rounded-xl shrink-0 animate-pulse"
          : "w-full max-w-[260px] h-[180px] rounded-xl animate-pulse mb-2"}
        style={{ background: "hsl(222 18% 14%)" }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={title}
      className={thumb
        ? "block w-9 h-9 rounded-xl overflow-hidden border shrink-0 transition-all active:scale-[0.97] cursor-zoom-in"
        : "block mb-2 rounded-xl overflow-hidden border transition-all active:scale-[0.99] cursor-zoom-in"}
      style={{ borderColor: "hsl(222 18% 22%)" }}
    >
      <img
        src={url}
        alt={title}
        loading="lazy"
        className={thumb
          ? "block w-full h-full object-cover"
          : "block max-w-full max-h-[280px] object-contain"}
        onError={() => {
          if (retried) {
            setFailed(true);
          } else {
            // Signed URL expirou entre a emissão e o render — re-assina 1x.
            setRetried(true);
          }
        }}
      />
    </button>
  );
};

export default DeliverableChatImage;
